import React from "react";
import cn from "classnames";
import { Controller, FieldValues } from "react-hook-form";
import { MultiValue, SingleValue } from "react-select";
import Select from "Components/UI/Select/Select";
import { DefaultOption } from "../Select/types";
import { SelectFieldProps } from "./types";
import styles from "./SelectField.module.scss";
import AsyncSelect from "../Select/AsyncSelect";

const SelectFieldNew = <T = DefaultOption, R extends FieldValues = any>(
  props: SelectFieldProps<T, R>
) => {
  const {
    name,
    options = [],
    defaultValue,
    defaultValueToController,
    withController,
    control,
    label,
    isCreatable = false,
    optionValue = "value",
    downLabel,
    disabled,
    menuPortalTarget,
    isAsyncPaginated = false,
  } = props;

  const getItem = (value: string | string[]) => {
    const isNested = options.some((item) => item.options);
    let flatOptions = options;
    if (isNested) {
      flatOptions = [];
      options.forEach((item) => {
        flatOptions = [...flatOptions, ...item.options];
      });
    }

    if (Array.isArray(value)) {
      return flatOptions.filter((option) =>
        value.includes(option[optionValue])
      );
    }
    return flatOptions.find((option) => option[optionValue] === value);
  };

  const getValue = (value?: MultiValue<T> | SingleValue<T>) => {
    if (!value) return undefined;
    else if (Array.isArray(value))
      return value.map((item) => item[optionValue]);
    else {
      const singleValue = value as any;
      return singleValue[optionValue];
    }
  };
  // nEED

  const GeneralSelectField = isAsyncPaginated ? AsyncSelect : Select;

  return (
    <div className={cn(styles.select)}>
      {label && (
        <label
          className={styles.select__label}
          id={name}
          style={{ color: props.labelColor }}
        >
          {label}
          {props.required && <span className={styles.select__error}>*</span>}
        </label>
      )}
      {withController ? (
        <Controller
          control={control}
          name={name!}
          disabled={disabled}
          defaultValue={defaultValueToController as any}
          render={({ field, fieldState }) => (
            <>
              <GeneralSelectField
                {...props}
                {...field}
                value={getItem(field.value)}
                onChange={(value) => {
                  field.onChange(getValue(value));
                }}
                isSearchable
                menuPortalTarget={menuPortalTarget}
                isCreatable={isCreatable}
                isDisabled={disabled}
                {...(isAsyncPaginated && { defaultOptions: true })}
              />
              {fieldState.error && (
                <div className={styles.select__error}>
                  {fieldState.error.message}
                </div>
              )}
            </>
          )}
        />
      ) : (
        <GeneralSelectField
          {...props}
          value={props.value}
          menuPortalTarget={menuPortalTarget}
          defaultValue={defaultValue}
          onChange={(value: any, actionMeta) =>
            props?.onChange?.(value, actionMeta)
          }
          isCreatable={isCreatable}
          isDisabled={disabled}
          {...(isAsyncPaginated && { defaultOptions: true })}
        />
      )}
      {downLabel && (
        <label
          className={styles.select__label}
          id={name}
          style={{ color: props.labelColor }}
        >
          {downLabel}
        </label>
      )}
    </div>
  );
};

export default SelectFieldNew;
